import type { ICellRendererParams } from 'ag-grid-community'
import type React from 'react'

import { Badge, type BadgeVariant } from '@moment/design-system-ui/badge'
import { Children } from 'react'

import Checkbox from '@/components/atomic/checkbox'
import Icon from '@/components/atomic/icon'
import Input from '@/components/atomic/input'

const TextAndIconContainer = ({
  children,
  iconSide,
}: {
  children: React.ReactNode
  iconSide?: 'left' | 'right'
}) => {
  const [textElement, iconElement] = Children.toArray(children)
  return (
    <div className="flex items-center gap-1 w-full">
      {iconSide === 'right' ? (
        <>
          <span className="flex-1 truncate">{textElement}</span>
          <span className="shrink-0">{iconElement}</span>
        </>
      ) : (
        <>
          <span className="shrink-0">{iconElement}</span>
          <span className="flex-1 truncate">{textElement}</span>
        </>
      )}
    </div>
  )
}

export function getTextAndIconCellRenderer(
  params: ICellRendererParams,
  iconSide: 'left' | 'right',
): React.JSX.Element | null {
  if (!params.value?.text || !params.value?.iconName) {
    return null
  }
  return (
    <TextAndIconContainer iconSide={iconSide}>
      <span>{params.value.text}</span>
      <Icon iconName={params.value.iconName} size={'small'} />
    </TextAndIconContainer>
  )
}

export function getBadgeCellRenderer(
  params: ICellRendererParams,
): React.JSX.Element | null {
  if (!params.value?.label || !params.value?.variant) {
    return null
  }
  return (
    <Badge variant={params.value.variant as BadgeVariant}>
      {params.value.label}
    </Badge>
  )
}

export function getInputCellRenderer(placeholder: string): React.JSX.Element {
  return <Input size="small" placeholder={placeholder} />
}

export function getCheckboxCellRenderer(
  onCheckedChange: (value: boolean | 'indeterminate') => void,
  checked: boolean | 'indeterminate',
  isChildRow?: boolean,
  disabled?: boolean,
  tooltipText?: string,
): React.JSX.Element | null {
  if (isChildRow) return null
  return (
    <Checkbox
      onCheckedChange={onCheckedChange}
      checked={checked}
      disabled={disabled}
      tooltipText={tooltipText}
    />
  )
}

export function getIconCellRenderer(
  params: ICellRendererParams,
): React.JSX.Element | null {
  if (!params.value) {
    return null
  }
  return <Icon iconName={params.value} size="small" />
}
